<template>
  <div class="space-y-6 mt-4 bg-violet-100">
    <div class="flex flex-col gap-6 xl:flex-row xl:items-start">
      <div v-if="loading" class="w-full h-64 flex flex-col justify-center items-center xl:w-3/4">
        <h3 class="text-center">Loading...</h3>
      </div>
      <div class="w-full space-y-6 xl:w-3/4" v-else>
        <div class="flex flex-wrap items-center justify-end gap-3">
          <FilterDropdown multiple v-model="selectedUser" :items="userOptions" label="All Users" icon-name="BluePerson"
            title-field="name" subtitle-field="email" item-key="id" search-placeholder="Search users..."
            :select-all-value="'all'" :show-leading-visual-when-multiple="true" @select="handleUserSelect"
            @select-all="handleUserSelectAll" />
          <FilterDropdown v-model="selectedBranch" :items="branchOptions" label="All Branch" icon-name="BlueBranch"
            title-field="name" item-key="id" search-placeholder="Search branches..." :select-all-value="'all'"
            :multiple="true" :show-leading-visual="false" :loading="loadingBranches" @select="handleBranchSelect"
            @select-all="handleBranchSelectAll" />
          <DateFilter alignment="left" v-model="dateFilter" @apply="handleDateApply" @cancel="handleDateCancel" />
        </div>

        <div class="flex flex-col gap-4 xl:flex-row xl:items-stretch">
          <div class="flex w-full gap-4 xl:w-3/4">
            <div class="flex flex-col gap-4 lg:flex-row w-full lg:items-stretch">
              <div v-if="contactsCard" class="flex-1 bg-white p-5" :style="cardContainerStyle">
                <h2 class="text-gray-700 font-semibold mb-4">
                  {{ contactsCard.title }}
                </h2>
                <p class="mt-3 text-lg font-semibold text-slate-800">
                  {{ contactsCard.total }}
                </p>
                <p class="text-xs text-slate-600">
                  {{ contactsCard.subtitle }}
                </p>
                <div class="mt-5 flex h-2 w-full overflow-hidden rounded-full bg-slate-100">
                  <template v-if="!contactsSegmentsAllZero">
                    <div v-for="segment in contactsCard.segments" :key="segment.label" class="h-full flex-none" :style="{
                      width: `${segment.percent}%`,
                      backgroundColor: resolveColor(segment.color),
                    }"></div>
                  </template>
                  <div v-else class="h-full flex-1 rounded-full" style="background-color: #CBD5E1;"></div>
                </div>
                <div class="mt-5 space-y-3 text-sm text-slate-600">
                  <div v-for="segment in contactsCard.segments" :key="segment.label"
                    class="flex items-center justify-between">
                    <span class="flex items-center gap-2">
                      <span class="h-2 w-2 rounded-full border" :style="{
                        backgroundColor: contactsSegmentsAllZero ? '#CBD5E1' : resolveColor(segment.color),
                        borderColor: contactsSegmentsAllZero ? '#CBD5E1' : resolveColor(segment.color),
                      }"></span>
                      <span>{{ segment.label }}</span>
                    </span>
                    <span class="font-semibold text-slate-900">{{
                      segment.value
                      }}</span>
                  </div>
                </div>
              </div>
              <div v-if="partnersCard" class="flex-1 bg-white p-4" :style="cardContainerStyle">
                <div class="grid grid-rows-2">
                  <div class="bg-white px-5">
                    <!-- Partners Section -->
                    <div>
                      <h2 class="text-gray-700 font-semibold mb-4">Partners</h2>
                      <div class="flex justify-between text-center">
                        <div class="flex-1">
                          <div class="flex justify-center gap-2 items-center mb-2">
                            <Icon name="PartnerIcon" />
                            <div class="text-xl font-bold text-gray-900">
                              {{ partnersCard.total }}
                            </div>
                          </div>

                          <div class="text-sm text-gray-500">
                            Total Partners
                          </div>
                        </div>

                        <div class="mx-4" :style="dividerStyle"></div>

                        <div class="flex-1">
                          <div class="flex justify-center gap-2 items-center mb-2">
                            <Icon name="DatabaseIcon" />
                            <div class="text-xl font-bold text-gray-900">
                              {{ partnersCard.metrics[0].value }}
                            </div>
                          </div>
                          <div class="text-sm text-gray-500">
                            Imported from Database
                          </div>
                        </div>

                        <div class="border-r border-gray-200 mx-4"></div>

                        <div class="flex-1">
                          <div class="flex justify-center gap-2 items-center mb-2">
                            <Icon name="ManualIcon" />
                            <div class="text-xl font-bold text-gray-900">
                              {{ partnersCard.metrics[1].value }}
                            </div>
                          </div>
                          <div class="text-sm text-gray-500">
                            Added Manually
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="bg-white px-5 pt-2">
                    <!-- Products Section -->
                    <div>
                      <h2 class="text-gray-700 font-semibold mb-4">Products</h2>
                      <div class="flex justify-between text-center">
                        <div class="flex-1">
                          <div class="flex justify-center items-center gap-2 mb-2">
                            <Icon name="PartnerIcon" />
                            <div class="text-xl font-bold text-gray-900">
                              {{ productsCard.total }}
                            </div>
                          </div>

                          <div class="text-sm text-gray-500">
                            Total Products
                          </div>
                        </div>

                        <div class="mx-4" :style="dividerStyle"></div>

                        <div class="flex-1">
                          <div class="flex justify-center items-center gap-2 mb-2">
                            <Icon name="DatabaseIcon" />
                            <div class="text-xl font-bold text-gray-900">
                              {{ productsCard.metrics[0].value }}
                            </div>
                          </div>
                          <div class="text-sm text-gray-500">
                            Imported from Database
                          </div>
                        </div>

                        <div class="border-r border-gray-200 mx-4"></div>

                        <div class="flex-1">
                          <div class="flex justify-center items-center gap-2 mb-2">
                            <Icon name="ManualIcon" />
                            <div class="text-xl font-bold text-gray-900">
                              {{ productsCard.metrics[1].value }}
                            </div>
                          </div>
                          <div class="text-sm text-gray-500">
                            Added Manually
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="w-full xl:w-1/4 bg-white">
            <div class="bg-white p-5">
              <div v-if="leadFormsCard">
                <h2 class="text-gray-700 font-semibold mb-4">
                  {{ leadFormsCard.title }}
                </h2>
                <div class="mt-4 flex items-center flex-col gap-6">
                  <CircleProgress :value="leadFormsCard.value" :total="leadFormsCard.total" :color="leadFormsCard.color"
                    :trail-color="leadFormsCard.trailColor" size="lg" :show-percent="false">
                    <div class="text-xl font-semibold text-slate-900">
                      {{ leadFormsCard.value }}
                    </div>
                    <div class="text-xs text-slate-500">
                      {{ leadFormsCard.subtitle }}
                    </div>
                  </CircleProgress>
                  <div class="space-y-3 text-sm text-slate-600">
                    <div v-for="metric in leadFormsCard.metrics" :key="metric.label" class="flex items-center gap-2">
                      <span class="h-2 w-2 rounded-full border" :style="{
                        backgroundColor: resolveColor(metric.color),
                        borderColor: resolveColor(metric.color),
                      }"></span>
                      <span class="font-medium text-slate-800">{{
                        metric.label
                        }}</span>
                      <span class="font-semibold text-slate-900">{{
                        metric.value
                        }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="grid grid-cols-2 md:grid-cols-3 gap-4 lg:grid-cols-4 xl:grid-cols-4">
          <div class="grid grid-rows-2 gap-2">
            <div class="bg-white p-5">
              <p class="text-sm font-semibold text-slate-700">Application</p>
              <div class="mt-3 flex items-baseline gap-3">
                <span class="text-3xl font-semibold text-slate-900">{{
                  applicationCard.total
                  }}</span>
                <span class="text-sm text-slate-500">{{
                  applicationCard.totalLabel
                  }}</span>
              </div>
              <div class="mt-5">
                <div class="flex h-3 w-full overflow-hidden bg-slate-100">
                  <template v-if="!applicationSegmentsAllZero">
                    <div v-for="(segment, index) in applicationCard.segments" :key="segment.label" class="h-full"
                      :class="getApplicationSegmentRadiusClass(index)" :style="{
                        width: getApplicationSegmentWidth(segment.value) + '%',
                        backgroundColor: segment.color,
                      }"></div>
                  </template>
                  <div v-else class="h-full flex-1" style="background-color: #CBD5E1;"></div>
                </div>
              </div>
              <div class="mt-5 flex flex-wrap gap-x-6 gap-y-3 text-sm">
                <div v-for="segment in applicationCard.segments" :key="segment.label"
                  class="flex items-center gap-2 text-slate-600">
                  <span class="h-2 w-2 rounded-full border" :style="{
                    backgroundColor: resolveColor(segment.color),
                    borderColor: resolveColor(segment.color),
                  }"></span>
                  <span class="font-semibold text-slate-900">{{
                    segment.value
                    }}</span>
                  <span class="text-slate-500">{{ segment.label }}</span>
                </div>
              </div>
            </div>
            <div class="bg-white p-5">
              <div class="flex items-start justify-between">
                <p class="text-base font-semibold text-slate-700">
                  Interested Services
                </p>
                <span class="text-2xl font-semibold text-slate-900">{{
                  interestedServicesCard.total
                  }}</span>
              </div>
              <div class="mt-3 h-px w-full bg-slate-100"></div>
              <div class="mt-4">
                <div class="flex items-center justify-between text-sm">
                  <span class="text-slate-500">Conversion Rate</span>
                  <span class="text-sm font-semibold text-slate-900">{{ interestedServicesCard.conversionRate }}%</span>
                </div>
                <div class="mt-3 h-3 w-full rounded-full" :style="{
                  backgroundColor: interestedServicesCard.trackColor,
                }">
                  <div class="h-full rounded-full" :style="{
                    width: interestedServicesCard.conversionRate + '%',
                    backgroundColor: interestedServicesCard.progressColor,
                  }"></div>
                </div>
              </div>
            </div>
          </div>
          <div class="bg-white p-5">
            <div class="flex items-start justify-between">
              <p class="text-base font-semibold text-slate-700">Task</p>
              <span class="text-2xl font-semibold text-slate-900">{{
                taskCard.total
                }}</span>
            </div>
            <p class="mt-1 text-sm text-slate-500">{{ taskCard.totalLabel }}</p>
            <div class="mt-5 space-y-4">
              <div
                v-for="task in (taskCard.showAll ? taskCard.items : taskCard.items.slice(0, 5))"
                :key="task.key"
              >
                <div class="flex items-center justify-between text-sm">
                  <span class="text-slate-500">{{ task.label }}</span>
                  <span class="text-sm font-semibold text-slate-900">{{ task.value }}</span>
                </div>
                <div class="relative mt-2 h-3 w-full rounded-full" :style="{ backgroundColor: taskCard.trackColor }">
                  <div class="h-full rounded-full" :style="{
                    width: getTaskItemWidth(task.value) + '%',
                    backgroundColor: task.color,
                  }"></div>
                </div>
              </div>
              <button
                v-if="taskCard.items.length > 5"
                class="mt-1 text-xs font-medium text-indigo-600 hover:text-indigo-800"
                @click="taskCard.showAll = !taskCard.showAll"
              >
                {{ taskCard.showAll ? 'View Less' : `View More (${taskCard.items.length - 5} more)` }}
              </button>
            </div>
          </div>
          <div class="bg-white p-5">
            <p class="text-base font-semibold text-slate-700">Document</p>
            <div class="mt-3">
              <span class="text-2xl font-semibold text-slate-900">{{
                documentCard.total
                }}</span>
              <p class="text-sm text-slate-500">
                {{ documentCard.totalLabel }}
              </p>
            </div>
            <div class="mt-5 space-y-4">
              <div
                v-for="item in (documentCard.showAll ? documentCard.items : documentCard.items.slice(0, 5))"
                :key="item.key"
              >
                <div class="flex items-center justify-between text-sm">
                  <span class="text-slate-500">{{ item.label }}</span>
                  <span class="text-sm font-semibold text-slate-900">{{ item.value }}</span>
                </div>
                <div class="mt-2 h-3 w-full rounded-full" :style="{ backgroundColor: documentCard.trackColor }">
                  <div class="h-full rounded-full" :style="{
                    width: getDocumentItemWidth(item.value) + '%',
                    backgroundColor: item.color,
                  }"></div>
                </div>
              </div>
              <button
                v-if="documentCard.items.length > 5"
                class="mt-1 text-xs font-medium text-indigo-600 hover:text-indigo-800"
                @click="documentCard.showAll = !documentCard.showAll"
              >
                {{ documentCard.showAll ? 'View Less' : `View More (${documentCard.items.length - 5} more)` }}
              </button>
            </div>
          </div>
          <div class="bg-white p-5">
            <p class="text-base font-semibold text-slate-700">Notes & Terms</p>
            <div class="mt-3">
              <span class="text-2xl font-semibold text-slate-900">{{
                notesTermsCard.total
                }}</span>
              <p class="text-sm text-slate-500">
                {{ notesTermsCard.totalLabel }}
              </p>
            </div>
            <div class="mt-5 space-y-4">
              <div v-for="item in notesTermsCard.items" :key="item.key">
                <div class="flex items-center justify-between text-sm">
                  <span class="text-slate-500">{{ item.label }}</span>
                  <span class="text-sm font-semibold text-slate-900">{{
                    item.value
                    }}</span>
                </div>
                <div class="relative mt-2 h-3 w-full rounded-full"
                  :style="{ backgroundColor: notesTermsCard.trackColor }">
                  <div class="h-full rounded-full" :style="{
                    width: getNotesItemWidth(item.value) + '%',
                    backgroundColor: item.color,
                  }"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="grid grid-cols-2 gap-4">
          <div class="flex gap-4">
            <div class="bg-white p-5 w-1/2" :style="cardContainerStyle">
              <div class="flex items-start justify-between">
                <div>
                  <span class="text-lg font-semibold text-slate-800">{{
                    appointmentsCard.total
                    }}</span>
                  <p class="mt-1 text-sm text-slate-500">
                    {{ appointmentsCard.label }}
                  </p>
                </div>
                <div class="flex h-10 w-10 items-center justify-center rounded-full">
                  <Icon :name="appointmentsCard.iconName" />
                </div>
              </div>
            </div>
            <div class="bg-white p-5 w-1/2" :style="cardContainerStyle">
              <div class="flex items-start justify-between">
                <div>
                  <span class="text-lg font-semibold text-slate-800">{{
                    tagsAddedCard.total
                    }}</span>
                  <p class="mt-1 text-sm text-slate-500">
                    {{ tagsAddedCard.label }}
                  </p>
                </div>
                <span>
                  <Icon :name="tagsAddedCard.iconName" />
                </span>
              </div>
            </div>
          </div>
          <div class="bg-white p-5" :style="cardContainerStyle">
            <p class="text-base font-semibold text-slate-700">Office Checkin</p>
            <div class="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
              <div v-for="item in officeCheckinCard.items" :key="item.key"
                class="flex flex-col items-center justify-center gap-2">
                <span class="flex items-center justify-center gap-2">
                  <Icon :name="item.iconName" />
                  <div class="text-base font-semibold text-slate-900">
                    {{ item.value }}
                  </div>
                </span>
                <div>
                  <div class="text-xs text-slate-500">{{ item.label }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="grid grid-cols-2 gap-4">
          <div class="flex flex-col gap-4">
            <div class="w-full">
              <div class="flex gap-4">
                <div class="bg-white p-5 w-1/2" :style="cardContainerStyle">
                  <p class="text-base font-semibold text-slate-700">Workflow</p>
                  <div class="mt-4 flex items-center gap-6">
                    <CircleProgress :value="workflowCard.value" :total="workflowCard.total" :color="workflowCard.color"
                      :trail-color="workflowCard.trailColor" size="lg" :show-percent="false">
                      <div class="text-2xl font-semibold text-slate-900">
                        {{ workflowCard.total }}
                      </div>
                      <div class="text-xs text-slate-500">
                        {{ workflowCard.subtitle }}
                      </div>
                    </CircleProgress>
                    <div class="space-y-3 text-sm text-slate-600">
                      <div class="flex flex-col" v-for="metric in workflowCard.metrics" :key="metric.label">

                        <div class="font-semibold text-slate-900">
                          {{ metric.value }}
                        </div>
                        <div class="flex items-center gap-1 w-full">
                          <div class="h-2 w-2 rounded-full border" :style="{
                            backgroundColor: resolveColor(metric.color),
                            borderColor: resolveColor(metric.color),
                          }"></div>
                          <span class="text-slate-500">{{ metric.label }}</span>

                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="bg-white p-5 w-1/2" :style="cardContainerStyle">
                  <p class="text-base font-semibold text-slate-700">
                    Quotation
                  </p>
                  <div class="mt-4 space-y-4">
                    <div v-for="item in quotationCard.items" :key="item.key" class="flex items-center gap-3">
                      <div class="flex items-center gap-3">
                        <div>
                          <Icon :name="item.iconName" />
                        </div>
                        <div class="text-2xl font-semibold text-slate-900">
                          {{ item.value }}
                        </div>
                      </div>
                      <div class="text-sm text-slate-500">{{ item.label }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="w-full">
              <div class="flex gap-4">
                <div class="bg-white p-5 w-1/2" :style="cardContainerStyle">
                  <p class="text-base font-semibold text-slate-700">
                    Document Checklist
                  </p>
                  <div class="mt-3">
                    <span class="text-2xl font-semibold text-slate-900">{{
                      documentChecklistCard.total
                      }}</span>
                    <span class="ml-2 text-sm text-slate-500">{{
                      documentChecklistCard.totalLabel
                      }}</span>
                  </div>
                  <div class="mt-4 h-3 w-full rounded-full" :style="{
                    backgroundColor: documentChecklistCard.inactiveColor,
                  }">
                    <div class="h-full rounded-full" :style="{
                      width: getDocumentChecklistActiveWidth() + '%',
                      backgroundColor: documentChecklistCard.activeColor,
                    }"></div>
                  </div>
                  <div class="mt-4 flex items-center gap-6 text-sm">
                    <div class="flex items-center gap-2">
                      <div class="h-2 w-2 rounded-full border" :style="{
                        backgroundColor: documentChecklistCard.activeColor,
                        borderColor: documentChecklistCard.activeColor,
                      }"></div>
                      <span class="font-semibold text-slate-900">{{
                        documentChecklistCard.active
                        }}</span>
                      <span class="text-slate-500">Active</span>
                    </div>
                    <div class="flex items-center gap-2">
                      <div class="h-2 w-2 rounded-full border" :style="{
                        backgroundColor: documentChecklistCard.inactiveColor,
                        borderColor: documentChecklistCard.inactiveColor,
                      }"></div>
                      <span class="font-semibold text-slate-900">{{
                        documentChecklistCard.inactive
                        }}</span>
                      <span class="text-slate-500">Inactive</span>
                    </div>
                  </div>
                  <div class="mt-5 border-t border-slate-100 pt-4 text-sm text-slate-500">
                    <div class="flex items-center justify-between">
                      <span>Active Document Type</span>
                      <span class="text-base font-semibold text-slate-900">{{
                        documentChecklistCard.activeDocumentType
                        }}</span>
                    </div>
                  </div>
                </div>
                <div class="bg-white p-5 w-1/2" :style="cardContainerStyle">
                  <div class="flex items-start justify-between">
                    <div>
                      <p class="text-base font-semibold text-slate-700">
                        Payment Schedule
                      </p>
                      <div class="mt-8 flex items-center gap-3">
                        <div>
                          <span class="text-3xl font-semibold text-slate-900">{{
                            paymentScheduleCard.total
                            }}</span>
                          <p class="mt-1 text-sm text-slate-500">
                            {{ paymentScheduleCard.label }}
                          </p>
                        </div>
                        <Icon :name="paymentScheduleCard.iconName" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="bg-white p-5" :style="cardContainerStyle">
            <p class="text-base font-semibold text-slate-700 mb-4">Invoice</p>
            <div class="flex items-center justify-between rounded-lg border border-slate-200 px-5 py-3">
              <div class="flex items-center gap-3 text-slate-600">
                <Icon name="Invoices" />
                <span class="font-medium">Total Invoices</span>
              </div>
              <div class="text-sm font-semibold text-slate-900">
                {{ invoiceCard.total }} Invoices
              </div>
            </div>
            <div class="mt-5 overflow-x-auto rounded-lg border border-slate-200">
              <table class="min-w-full divide-y divide-slate-200 text-sm text-slate-600">
                <thead class="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <tr>
                    <th class="px-5 py-3 text-left">Invoice Type</th>
                    <th class="px-5 py-3 text-right">Paid</th>
                    <th class="px-5 py-3 text-right">Unpaid</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-slate-100 bg-white">
                  <tr v-for="row in invoiceCard.rows" :key="row.type">
                    <td class="px-5 py-3 text-slate-700">{{ row.type }}</td>
                    <td class="px-5 py-3 text-right font-semibold text-slate-900">
                      {{ row.paid }}
                    </td>
                    <td class="px-5 py-3 text-right text-slate-500">
                      {{ row.unpaid }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div class="grid grid-cols-2 gap-4">
          <div class="flex gap-4">
            <div class="bg-white p-5 w-1/2" :style="cardContainerStyle">
              <p class="text-base font-semibold text-slate-700">Template</p>
              <div class="mt-4 space-y-3">
                <div v-for="item in templateCard.items" :key="item.key"
                  class="flex flex-col rounded-lg border border-slate-200 px-4 py-3"
                  :style="{ borderLeft: `2px solid ${item.accentColor}` }">
                  <div class="flex items-center gap-3">
                    <Icon :name="item.iconName" />
                    <span class="text-2xl font-semibold text-slate-900">{{
                      item.value
                      }}</span>
                  </div>
                  <span class="text-sm text-slate-500">{{ item.label }}</span>
                </div>
              </div>
            </div>
            <div class="bg-white p-5 w-1/2" :style="cardContainerStyle">
              <p class="text-base font-semibold text-slate-700">Agents</p>
              <div class="mt-4 flex items-center gap-6">
                <div class="relative h-32 w-32">
                  <CircleProgress :value="agentsCard.outerValue" :total="agentsCard.outerTotal" :thickness="10"
                    :color="agentsCard.outerColor" :trail-color="agentsCard.outerTrailColor" :show-percent="false"
                    size="lg">
                    <CircleProgress :value="agentsCard.innerValue" :total="agentsCard.innerTotal" :thickness="10"
                      :color="agentsCard.innerColor" :trail-color="agentsCard.innerTrailColor" :show-percent="false"
                      size="md">
                      <div class="text-xl font-semibold text-slate-900 text-center">
                        {{ agentsCard.total }}
                        <div class="text-xs font-normal text-slate-500">
                          Agents
                        </div>
                      </div>
                    </CircleProgress>
                  </CircleProgress>
                </div>
                <div class="space-y-5 text-sm text-slate-500">
                  <div v-for="metric in agentsCard.metrics" :key="metric.label" class="space-y-1">
                    <div class="text-lg font-semibold text-slate-900">
                      {{ metric.value }}
                    </div>
                    <div class="flex items-center gap-2">
                      <div class="h-2 w-2 rounded-full border" :style="{
                        backgroundColor: resolveColor(metric.color),
                        borderColor: resolveColor(metric.color),
                      }"></div>
                      <span>{{ metric.label }}</span>
                    </div>
                    <div class="h-px w-full bg-slate-100" v-if="!metric.last"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="bg-white p-5 w-full" :style="cardContainerStyle">
            <p class="text-base font-semibold text-slate-700">Agent Connect</p>
            <div class="mt-4 grid grid-cols-2 gap-4 lg:grid-cols-3">
              <div v-for="item in agentConnectCard.items" :key="item.key"
                class="flex items-center gap-3 rounded-lg border border-slate-200 px-4 py-3">
                <div>
                  <Icon :name="item.iconName" />
                </div>
                <div>
                  <div class="text-lg font-semibold text-slate-900">
                    {{ item.value }}
                  </div>
                  <div class="text-xs text-slate-500">{{ item.label }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div class="bg-white p-5" :style="cardContainerStyle">
            <p class="text-base font-semibold text-slate-700">SMS Usage</p>
            <div class="mt-4 flex items-center">
              <div v-for="(item, index) in smsUsageCard.items" :key="item.key" class="flex items-center">
                <div class="flex items-start gap-3">
                  <span class="mt-1">
                    <Icon :name="item.iconName" />
                  </span>
                  <div>
                    <div class="text-2xl font-semibold text-slate-900">
                      {{ item.value }}
                    </div>
                    <div class="text-sm text-slate-500">{{ item.label }}</div>
                  </div>
                </div>
                <div v-if="index !== smsUsageCard.items.length - 1" class="mx-1" :style="{
                  width: '1px',
                  height: '36.014px',
                  backgroundColor: '#F1F5F9',
                }"></div>
              </div>
            </div>
          </div>
          <div class="bg-white p-5" :style="cardContainerStyle">
            <div class="flex items-start justify-between">
              <p class="text-base font-semibold text-slate-700">
                WhatsApp Usage
              </p>
              <div class="flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium" :style="{
                backgroundColor: whatsAppUsageCard.statusBackground,
                color: whatsAppUsageCard.statusColor,
              }">
                <span class="h-2 w-2 rounded-full" :style="{ backgroundColor: whatsAppUsageCard.statusColor }"></span>
                {{ whatsAppUsageCard.statusLabel }}
              </div>
            </div>
            <div class="mt-4 flex items-center">
              <div v-for="(item, index) in whatsAppUsageCard.items" :key="item.key" class="flex items-center">
                <div class="flex items-start gap-3">
                  <span class="mt-1">
                    <Icon :name="item.iconName" />
                  </span>
                  <div>
                    <div class="text-2xl font-semibold text-slate-900">
                      {{ item.value }}
                    </div>
                    <div class="text-sm text-slate-500">{{ item.label }}</div>
                  </div>
                </div>
                <div v-if="index !== whatsAppUsageCard.items.length - 1" class="mx-6" :style="{
                  width: '1px',
                  height: '36.014px',
                  backgroundColor: '#F1F5F9',
                }"></div>
              </div>
            </div>
          </div>
          <div class="bg-white p-5" :style="cardContainerStyle">
            <p class="text-base font-semibold text-slate-700">Email Usage</p>
            <div class="mt-4 flex items-center">
              <div v-for="(item, index) in emailUsageCard.items" :key="item.key" class="flex items-center">
                <div class="flex items-start gap-3">
                  <span class="mt-1">
                    <Icon :name="item.iconName" />
                  </span>
                  <div>
                    <div class="text-2xl font-semibold text-slate-900">
                      {{ item.value }}
                    </div>
                    <div class="text-sm text-slate-500">{{ item.label }}</div>
                  </div>
                </div>
                <div v-if="index !== emailUsageCard.items.length - 1" class="mx-6" :style="{
                  width: '1px',
                  height: '36.014px',
                  backgroundColor: '#F1F5F9',
                }"></div>
              </div>
            </div>
          </div>
        </div>

        <div class="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div class="bg-white p-5" :style="cardContainerStyle">
            <p class="text-base font-semibold text-slate-700">
              SMS Campaign By Status
            </p>
            <div class="flex justify-center">
              <HalfDonutChart :segments="smsCampaignCard.chartSegments" :total-label="smsCampaignCard.totalLabel"
                :total-value="smsCampaignCard.total" :labels="segmentLabels" />
            </div>
          </div>
          <div class="bg-white p-5" :style="cardContainerStyle">
            <p class="text-base font-semibold text-slate-700">
              Email Campaign By Status
            </p>
            <div class="flex justify-center">
              <HalfDonutChart :segments="emailCampaignCard.chartSegments" :total-label="emailCampaignCard.totalLabel"
                :total-value="emailCampaignCard.total" :labels="segmentLabels" />
            </div>
          </div>
          <div class="bg-white p-5" :style="cardContainerStyle">
            <p class="text-base font-semibold text-slate-700">
              WhatsApp Campaign By Status
            </p>
            <div class="flex justify-center">
              <HalfDonutChart :segments="whatsAppCampaignCard.chartSegments"
                :total-label="whatsAppCampaignCard.totalLabel" :total-value="whatsAppCampaignCard.total"
                :labels="segmentLabels" />
            </div>
          </div>
        </div>
        <div class="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div v-for="card in campaignDeliveryCards" :key="card.title" class="bg-white p-5" :style="cardContainerStyle">
            <p class="text-base font-semibold text-slate-700">
              {{ card.title }}
            </p>
            <div class="mt-5 space-y-4 text-sm text-slate-500">
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-3 text-slate-600">
                  <Icon :name="card.iconName" />
                  <span class="text-sm font-medium text-slate-700">{{
                    card.totalLabel
                    }}</span>
                </div>
                <div class="text-base font-semibold text-slate-900">
                  {{ card.totalValue.toLocaleString() }}
                </div>
              </div>
              <div v-for="metric in card.metrics" :key="metric.label" class="flex items-center justify-between">
                <div class="flex items-center gap-3">
                  <Icon :name="metric.iconName" />
                  <span>{{ metric.label }}</span>
                </div>
                <div class="flex items-center gap-3">
                  <span class="text-sm font-semibold text-slate-900">{{
                    metric.value.toLocaleString()
                    }}</span>
                  <span class="text-xs font-medium" :style="{ color: '#29CC6A' }">{{ metric.percent }}%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="grid grid-cols-2 gap-4">
          <div class="flex flex-col gap-4">
            <div class="w-full">
              <div class="flex gap-4">
                <div class="bg-white p-5 w-1/2">
                  <div class="flex items-start justify-between">
                    <div>
                      <span class="text-3xl font-semibold text-slate-900">{{
                        clientsCard.total
                        }}</span>
                      <p class="mt-1 text-sm text-slate-500">
                        {{ clientsCard.label }}
                      </p>
                      <p class="mt-1 text-sm text-slate-500">Activated</p>
                    </div>
                    <div>
                      <Icon :name="clientsCard.iconName" />
                    </div>
                  </div>
                </div>
                <div class="bg-white p-5 w-1/2">
                  <div class="flex items-start justify-between">
                    <div>
                      <span class="text-3xl font-semibold text-slate-900">{{
                        CompanyEmailCard.total
                        }}</span>
                      <p class="mt-1 text-sm text-slate-500">
                        {{ CompanyEmailCard.label }}
                      </p>
                      <p class="mt-1 text-sm text-slate-500">Added</p>
                    </div>
                    <div>
                      <Icon :name="CompanyEmailCard.iconName" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div v-for="table in tableCardsByPosition.left" :key="table.title" class="w-full">
              <div class="bg-white p-5" :style="cardContainerStyle">
                <p :class="[
                  'text-base font-semibold text-slate-700',
                  table.titleClass,
                ]">
                  {{ table.title }}
                </p>
                <div class="mt-4 overflow-x-auto rounded-lg border border-slate-200">
                  <table class="min-w-full divide-y divide-slate-200 text-sm text-slate-600">
                    <thead class="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      <tr>
                        <th v-for="column in table.columns" :key="column.key" :class="[
                          tableHeaderBaseClass,
                          column.align === 'right'
                            ? 'text-right'
                            : 'text-left',
                        ]">
                          {{ column.label }}
                        </th>
                      </tr>
                    </thead>
                    <tbody class="divide-y divide-slate-100 bg-white">
                      <tr v-for="row in table.rows" :key="row[table.rowKey]">
                        <td v-for="column in table.columns" :key="column.key" :class="[
                          tableCellBaseClass,
                          column.align === 'right'
                            ? 'text-right'
                            : 'text-left',
                          column.cellClass,
                        ]">
                          {{ row[column.key] }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
          <div class="bg-white p-5 flex flex-col h-full" :style="cardContainerStyle">
            <p class="text-base font-semibold text-slate-700">Agent Portal</p>
            <div class="mt-4 grid grid-cols-2 gap-4 flex-1">
              <div v-for="item in agentPortalCard.items" :key="item.key"
                class="flex flex-col items-center justify-center rounded-lg border border-slate-200 px-4 py-5 h-full">
                <div class="flex items-center gap-2 mb-1">
                  <Icon :name="item.iconName" />
                  <span class="text-2xl font-semibold text-slate-900">{{ item.value.toLocaleString() }}</span>
                </div>
                <div class="text-xs text-center text-slate-500">{{ item.label }}</div>
              </div>
            </div>
          </div>
          <div v-for="table in tableCardsByPosition.right" :key="table.title" class="bg-white p-5"
            :style="cardContainerStyle">
            <p :class="[
              'text-base font-semibold text-slate-700',
              table.titleClass,
            ]">
              {{ table.title }}
            </p>
            <div class="mt-5 overflow-hidden rounded-lg border border-slate-200">
              <table class="min-w-full divide-y divide-slate-200 text-sm text-slate-600">
                <thead class="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <tr>
                    <th v-for="column in table.columns" :key="column.key" :class="[
                      tableHeaderBaseClass,
                      column.align === 'right' ? 'text-right' : 'text-left',
                    ]">
                      {{ column.label }}
                    </th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-slate-100 bg-white">
                  <tr v-for="row in table.rows" :key="row[table.rowKey]">
                    <td v-for="column in table.columns" :key="column.key" :class="[
                      tableCellBaseClass,
                      column.align === 'right' ? 'text-right' : 'text-left',
                      column.cellClass,
                    ]">
                      {{ row[column.key] }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div class="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div class="bg-white p-5" :style="cardContainerStyle">
            <p class="text-base font-semibold text-slate-700">Classroom</p>
            <div class="mt-4 overflow-x-auto rounded-lg border border-slate-200">
              <table class="min-w-full divide-y divide-slate-200 text-sm text-slate-600">
                <thead class="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <tr>
                    <th :class="[tableHeaderBaseClass, 'text-left']">
                      Category
                    </th>
                    <th :class="[tableHeaderBaseClass, 'text-right']">Count</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-slate-100 bg-white">
                  <tr v-for="row in classroomCard.rows" :key="row.category">
                    <td :class="[tableCellBaseClass, 'text-slate-700']">
                      {{ row.category }}
                    </td>
                    <td :class="[
                      tableCellBaseClass,
                      'text-right',
                      'font-semibold',
                      'text-slate-900',
                    ]">
                      {{ row.count }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div class="bg-white p-5" :style="cardContainerStyle">
            <p class="text-base font-semibold text-slate-700">Automation</p>
            <div class="mt-4 space-y-3">
              <div v-for="item in automationCard.items" :key="item.key"
                class="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3">
                <div class="flex items-center gap-3">
                  <span>
                    <Icon :name="item.iconName" />
                  </span>
                  <span class="text-sm font-medium text-slate-600 whitespace-nowrap">{{ item.label }}</span>
                </div>
                <div class="flex items-center gap-3">
                  <span class="text-base font-semibold text-slate-900">{{
                    item.value
                    }}</span>
                </div>
              </div>
            </div>
          </div>
          <div class="bg-white p-5" :style="cardContainerStyle">
            <p class="text-base font-semibold text-slate-700">Events</p>
            <div class="mt-4 space-y-3">
              <div v-for="item in eventsCard.items" :key="item.key"
                class="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3">
                <div class="flex items-center gap-3">
                  <span>
                    <Icon :name="item.iconName" />
                  </span>
                  <span class="text-sm font-medium text-slate-600 whitespace-nowrap">{{ item.label }}</span>
                </div>
                <span class="text-base font-semibold text-slate-900">{{
                  item.value
                  }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <aside class="w-full bg-white p-5 xl:w-1/4" :style="cardContainerStyle">
        <div class="p-4 w-full max-w-sm">
          <!-- Header -->
          <div class="flex items-center justify-between mb-4">
            <h2 class="text-slate-800 font-medium text-lg">Activity Log</h2>
            <DateFilter v-model="activityDateFilter" @apply="handleActivityDateApply" @cancel="handleActivityDateCancel"
              alignment="right" />
          </div>

          <template v-if="hasLogs">
            <div class="space-y-5 text-sm">
              <div v-for="(items, date) in groupedLogs" :key="date">
                <p class="text-gray-500 font-medium mb-2">{{ date }}</p>

                <div class="relative">
                  <span class="absolute top-0 bottom-0 w-px bg-gray-200" :style="{ left: '10px' }"></span>

                  <div v-for="(item, index) in items" :key="item.id || index" class="relative mb-4">
                    <div
                      class="absolute w-11 h-11 rounded-full border border-neutral-300 bg-white flex items-center justify-center"
                      :style="{
                        height: '2.625rem',
                        width: '2.625rem',
                        left: '-8px',
                      }">
                      <img v-if="item.user && item.user.avatar" :src="item.user.avatar" alt="avatar"
                        class="w-11 h-11 rounded-full" />
                      <span v-else
                        class="text-[10px] font-semibold w-11 h-11 flex items-center justify-center rounded-full"
                        :class="resolveInitialColor(getInitials(item.user))" :style="{
                          height: '2.625rem',
                          width: '2.625rem',
                          fontSize: '1rem',
                        }">
                        {{ getInitials(item.user) }}
                      </span>
                    </div>

                    <div class="ml-16">
                      <p class="text-slate-700 leading-snug text-base">
                        <span class="font-semibold">{{
                          (item.user && item.user.name) || "Unknown User"
                          }}</span>
                        {{ formatActivityAction(item.action) }}
                        <span v-if="item.target" class="text-blue-600 font-medium hover:underline cursor-pointer">
                          {{ item.target }}
                        </span>
                      </p>
                      <p class="text-gray-400 text-xs mt-1">{{ item.time }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
          <div v-else class="flex flex-col items-center justify-center text-center py-12 px-4 gap-3">
            <Icon name="EmptyLog" />
            <p class="text-slate-600 font-semibold text-sm">
              Looks like there are no logs for this date
            </p>
            <p class="text-slate-400 text-xs">
              Try selecting a different date or removing filters.
            </p>
          </div>
        </div>
      </aside>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import CircleProgress from "../core/CircleProgress.vue";
import DateFilter from "../DateFilter.vue";
import FilterDropdown from "../FilterDropdown.vue";
import Icon from "../core/Icon.vue";
import HalfDonutChart from "../HalfDonutChart.vue";

export default {
  name: "OverviewTab",
  components: {
    CircleProgress,
    DateFilter,
    FilterDropdown,
    Icon,
    HalfDonutChart,
  },
  props: {
    tenantId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      dateFilter: {
        preset: null,
        range: { from: "", to: "" },
      },
      activityDateFilter: {
        preset: null,
        range: { from: "", to: "" },
      },
      selectedUser: null,
      selectedBranch: [],
      userOptions: [],
      loadingUsers: false,
      errorUsers: null,
      branchOptions: [],
      loadingBranches: false,
      loading: false,
      errorBranches: null,
      applicationCard: {
        total: 0,
        totalLabel: "Total Application",
        segments: [
          { label: "Discontinued", value: 0, color: "#F97070" },
          { label: "Completed", value: 0, color: "#22C55E" },
          { label: "In Progress", value: 0, color: "#8B5CF6" },
        ],
      },
      interestedServicesCard: {
        total: 0,
        conversionRate: 0,
        progressColor: "#22C55E",
        trackColor: "#E4E2FF",
      },
      taskCard: {
        total: 0,
        totalLabel: "Total Task",
        trackColor: "#E4E2FF",
        items: [],
        showAll: false,
      },
      documentCard: {
        total: 0,
        totalLabel: "Total Document",
        trackColor: "#E4E2FF",
        items: [],
        showAll: false,
      },
      notesTermsCard: {
        total: 0,
        totalLabel: "Total Notes",
        trackColor: "#E4E2FF",
        items: [
          {
            key: "contacts",
            label: "Notes in Contacts",
            value: 0,
            color: "#8B5CF6",
          },
          {
            key: "applications",
            label: "Notes in Applications",
            value: 0,
            color: "#F472B6",
          },
          {
            key: "officeCheckIn",
            label: "Notes in Office Check In",
            value: 0,
            color: "#3B82F6",
            avatars: [
              { initials: "P", color: "#F97316" },
              { initials: "M", color: "#8B5CF6" },
            ],
          },
          {
            key: "agents",
            label: "Notes in Agents",
            value: 0,
            color: "#22C55E",
          },
          {
            key: "partners",
            label: "Notes in Partners",
            value: 0,
            color: "#FACC15",
          },
        ],
      },
      appointmentsCard: {
        total: 0,
        label: "Appointments",
        iconName: "Appointment",
      },
      clientsCard: {
        total: 0,
        label: "Clients",
        iconName: "Clients",
      },
      CompanyEmailCard: {
        total: 0,
        label: "Company Email",
        iconName: "Mails",
      },
      classroomCard: {
        rows: [
          { category: "Total Classroom Session", count: 0 },
          { category: "Not Started Classroom", count: 0 },
          { category: "In Progress Classroom", count: 0 },
          { category: "Archived Classroom", count: 0 },
          { category: "Total Enrolled Students", count: 0 },
        ],
      },
      automationCard: {
        items: [
          {
            key: "totalSetup",
            label: "Total Automation Setup",
            value: 0,
            iconName: "TotalAutomation",
          },
          {
            key: "active",
            label: "Active Automation",
            value: 0,
            iconName: "ActiveAutomationSetup",
          },
          {
            key: "inactive",
            label: "Inactive Automation",
            value: 0,
            iconName: "InactiveAutomation",
          },
          {
            key: "total",
            label: "Total Automation",
            value: 0,
            iconName: "ActiveAutomation",
          },
        ],
      },
      eventsCard: {
        items: [
          {
            key: "total",
            label: "Total Events",
            value: 0,
            iconName: "TotalEvents",
          },
          {
            key: "published",
            label: "Published Events",
            value: 0,
            iconName: "PublishedEvents",
          },
          {
            key: "drafts",
            label: "Draft Events",
            value: 0,
            iconName: "DraftsEvents",
          },
          {
            key: "archived",
            label: "Archived Events",
            value: 0,
            iconName: "ArchivedEvents",
          },
          {
            key: "contacts",
            label: "Contacts added from Events",
            value: 0,
            iconName: "ContactEvents",
          },
        ],
      },
      tagsAddedCard: {
        total: 0,
        label: "Tags Added",
        iconName: "Task",
      },
      officeCheckinCard: {
        items: [
          {
            key: "total",
            label: "Total",
            value: 0,
            iconName: "CheckInTotal",
            iconBackground: "#F0E4FF",
          },
          {
            key: "unassigned",
            label: "Unassigned",
            value: 0,
            iconName: "Unassigned",
            iconBackground: "#FEF3C7",
          },
          {
            key: "waiting",
            label: "Waiting",
            value: 0,
            iconName: "Waiting",
            iconBackground: "#DBEAFE",
          },
          {
            key: "attending",
            label: "Attending",
            value: 0,
            iconName: "Attending",
            iconBackground: "#FEE2E2",
          },
          {
            key: "completed",
            label: "Completed",
            value: 0,
            iconName: "Completed",
            iconBackground: "#DCFCE7",
          },
        ],
      },
      workflowCard: {
        total: 0,
        subtitle: "Workflow",
        value: 0,
        color: "#22C55E",
        trailColor: "#0EA5E9",
        metrics: [
          { label: "Active Workflow", value: "0", color: "bg-emerald-500" },
          { label: "Inactive Workflow", value: "0", color: "bg-sky-500" },
        ],
      },
      quotationCard: {
        items: [
          {
            key: "template",
            label: "Quotation Template",
            value: 0,
            iconName: "TemplateQuotation",
          },
          {
            key: "created",
            label: "Quotation Created",
            value: 0,
            iconName: "CreatedQuotation",
          },
          {
            key: "sent",
            label: "Quotation Sent",
            value: 0,
            iconName: "SentQuotation",
          },
        ],
      },
      invoiceCard: {
        total: 0,
        rows: [
          { type: "Net Claim Commission Invoice", paid: 0, unpaid: 0 },
          { type: "Gross Claim Commission Invoice", paid: 0, unpaid: 0 },
          { type: "Client General Invoice", paid: 0, unpaid: 0 },
          { type: "Partner General Invoice", paid: 0, unpaid: 0 },
          { type: "Payable Income Sharing", paid: 0, unpaid: 0 },
          { type: "Receivable Income Sharing", paid: 0, unpaid: 0 },
          { type: "Group Invoice", paid: 0, unpaid: 0 },
        ],
      },
      dataImportCard: {
        rows: [
          {
            category: "Partner & Product",
            total: 0,
            completed: 0,
            inProgress: 0,
          },
          { category: "Contact", total: 0, completed: 0, inProgress: 0 },
          {
            category: "Application",
            total: 0,
            completed: 0,
            inProgress: 0,
          },
        ],
      },
      documentChecklistCard: {
        total: 0,
        totalLabel: "Total Document Checklist",
        active: 0,
        inactive: 0,
        activeColor: "#22C55E",
        inactiveColor: "#0EA5E9",
        activeDocumentType: 0,
      },
      paymentScheduleCard: {
        total: 0,
        label: "Total Payment Schedule Created",
        iconName: "Payment",
        iconBackground: "#E6F6FE",
      },
      templateCard: {
        items: [
          {
            key: "sms",
            label: "SMS Template",
            value: 0,
            iconName: "SmsTemplate",
            accentColor: "#1D9BF0",
            iconBackground: "rgba(29, 155, 240, 0.12)",
          },
          {
            key: "email",
            label: "Email Template",
            value: 0,
            iconName: "EmailTemplate",
            accentColor: "#22C55E",
            iconBackground: "rgba(34, 197, 94, 0.12)",
          },
        ],
      },
      agentsCard: {
        total: 0,
        outerValue: 0,
        outerTotal: 0,
        outerColor: "#8B5CF6",
        outerTrailColor: "#F3E8FF",
        innerValue: 0,
        innerTotal: 0,
        innerColor: "#2563EB",
        innerTrailColor: "#E0F2FE",
        metrics: [
          { label: "Super Agents", value: 0, color: "#8B5CF6", last: false },
          { label: "Sub Agents", value: 0, color: "#2563EB", last: true },
        ],
      },
      agentConnectCard: {
        items: [
          {
            key: "connected",
            label: "Connected Agent",
            value: 0,
            iconName: "ConnectedAgent",
            iconBackground: "#F0FDF4",
          },
          {
            key: "applicationsReceived",
            label: "Applications Received",
            value: 0,
            iconName: "ApplicationsReceived",
            iconBackground: "#F5F3FF",
          },
          {
            key: "applicationsSent",
            label: "Applications Sent",
            value: 0,
            iconName: "ApplicationsSent",
            iconBackground: "#EFF6FF",
          },
          {
            key: "requestSent",
            label: "Request Sent",
            value: 0,
            iconName: "RequestSent",
            iconBackground: "#FFFBEB",
          },
          {
            key: "documentsExchanged",
            label: "Documents Exchanged",
            value: 0,
            iconName: "documentsExchanged",
            iconBackground: "#ECFDF5",
          },
          {
            key: "comments",
            label: "Comments",
            value: 0,
            iconName: "comments",
            iconBackground: "#FEF2F2",
          },
        ],
      },
      agentPortalCard: {
        items: [
          {
            key: "licensePurchased",
            label: "Total Licenses Purchased",
            value: 0,
            iconName: "PortalLicense",
          },
          {
            key: "totalActivatedPortals",
            label: "Total Activated Agent Portals Till Date",
            value: 0,
            iconName: "PortalActivated",
          },
          {
            key: "activePortals",
            label: "Active Agent Portals",
            value: 0,
            iconName: "PortalActive",
          },
          {
            key: "applicationsExchanged",
            label: "Application Exchanged",
            value: 0,
            iconName: "PortalApplications",
          },
        ],
      },
      smsUsageCard: {
        items: [
          {
            key: "total",
            label: "Total SMS Quota Purchased",
            value: 0,
            iconName: "BlueSMS",
          },
          {
            key: "remaining",
            label: "Remaining SMS Quota",
            value: 0,
            iconName: "RedSMS",
          },
          {
            key: "used",
            label: "Used SMS Quota",
            value: 0,
            iconName: "GreenSMS",
          },
        ],
      },
      whatsAppUsageCard: {
        statusLabel: "Not Integrated",
        statusColor: "#DC2626",
        statusBackground: "#FEF2F2",
        items: [
          { key: "sent", label: "Message Sent", value: 0, iconName: "MsgSent" },
          {
            key: "received",
            label: "Message Received",
            value: 0,
            iconName: "MsgRecieved",
          },
        ],
      },
      emailUsageCard: {
        items: [
          { key: "sent", label: "Email Sent", value: 0, iconName: "EmailSent" },
          {
            key: "received",
            label: "Email Received",
            value: 0,
            iconName: "EmailRecieved",
          },
        ],
      },
      smsCampaignCard: {
        total: 0,
        totalLabel: "SMS Campaign",
        chartSegments: [
          { key: "draft", value: 0, color: "#EAB308" },
          { key: "completed", value: 0, color: "#22C55E" },
          { key: "scheduled", value: 0, color: "#7C3AED" },
        ],
      },
      emailCampaignCard: {
        total: 0,
        totalLabel: "Email Campaign",
        chartSegments: [
          { key: "draft", value: 0, color: "#FACC15" },
          { key: "completed", value: 0, color: "#0EA5E9" },
          { key: "scheduled", value: 0, color: "#22C55E" },
        ],
      },
      whatsAppCampaignCard: {
        total: 0,
        totalLabel: "WhatsApp Campaign",
        chartSegments: [
          { key: "draft", value: 0, color: "#0EA5E9" },
          { key: "completed", value: 0, color: "#22C55E" },
          { key: "scheduled", value: 0, color: "#7C3AED" },
        ],
      },
      campaignDeliveryCards: [
        {
          title: "SMS Campaign By Delivery",
          totalLabel: "SMS Campaign Sent",
          iconName: "SmsSent",
          totalValue: 0,
          totalColor: "#8B5CF6",
          metrics: [
            {
              label: "Delivered",
              value: 0,
              percent: 0,
              iconName: "SmsDelivered",
            },
            { label: "Open", value: 0, percent: 0, iconName: "SmsOpen" },
            { label: "Click", value: 0, percent: 0, iconName: "SmsClicks" },
          ],
        },
        {
          title: "Email Campaign By Delivery",
          totalLabel: "Email Campaign Sent",
          iconName: "EmailSents",
          totalValue: 0,
          totalColor: "#8B5CF6",
          metrics: [
            {
              label: "Delivered",
              value: 0,
              percent: 0,
              iconName: "SmsDelivered",
            },
            { label: "Open", value: 0, percent: 0, iconName: "SmsOpen" },
            { label: "Click", value: 0, percent: 0, iconName: "SmsClicks" },
          ],
        },
        {
          title: "WhatsApp Campaign By Delivery",
          totalLabel: "WhatsApp Campaign Sent",
          iconName: "WhatsappSent",
          totalValue: 0,
          totalColor: "#0EA5E9",
          metrics: [
            {
              label: "Delivered",
              value: 0,
              percent: 0,
              iconName: "SmsDelivered",
            },
            { label: "Read", value: 0, percent: 0, iconName: "SmsOpen" },
          ],
        },
      ],
      segmentLabels: {
        draft: "In Draft",
        completed: "Completed",
        scheduled: "Scheduled",
      },
      cardContainerStyle: {
        borderRadius: "4px",
        border: "1px solid #F8FAFC",
        background: "#FFFFFF",
        boxShadow: "none",
      },
      dividerStyle: {
        width: "1px",
        backgroundColor: "#E2E8F0",
      },
      tableHeaderBaseClass: "px-5 py-3 whitespace-nowrap",
      tableCellBaseClass: "px-5 py-3 whitespace-nowrap",
      overviewCards: [
        {
          id: "contacts",
          type: "stacked",
          title: "Contacts",
          total: "0",
          subtitle: "Total Contacts",
          segments: [
            {
              label: "Total Clients",
              value: "0",
              percent: 0,
              color: "bg-sky-500",
            },
            {
              label: "Total Prospect",
              value: "0",
              percent: 0,
              color: "bg-emerald-500",
            },
            {
              label: "Total Enquiries",
              value: "0",
              percent: 0,
              color: "bg-amber-400",
            },
          ],
        },
        {
          id: "partners",
          type: "metrics",
          title: "Partners",
          total: "0",
          subtitle: "Total Partners",
          metrics: [
            { label: "Imported from Database", value: "0" },
            { label: "Added Manually", value: "0" },
          ],
        },
        {
          id: "products",
          type: "metrics",
          title: "Products",
          total: "0",
          subtitle: "Total Products",
          metrics: [
            { label: "Imported from Database", value: "0" },
            { label: "Added Manually", value: "0" },
          ],
        },
        {
          id: "leadForms",
          type: "circle",
          title: "Lead Forms",
          value: 0,
          total: 0,
          subtitle: "Lead Forms",
          color: "#10B981",
          trailColor: "#E5E7EB",
          metrics: [
            { label: "Active Lead Form", value: "0", color: "bg-emerald-500" },
            { label: "Inactive Lead Form", value: "0", color: "bg-rose-500" },
          ],
        },
      ],
      logs: [],
    };
  },
  computed: {
    ...mapState("TenantAnalyticsStore", {
      storedTenantUsers: (state) => state.tenantUsers,
      storedTenantBranches: (state) => state.tenantBranches,
      storedTenantFeatureUsage: (state) => state.tenantFeatureUsage,
      storedTenantActivityLog: (state) => state.tenantActivityLog,
    }),
    hasLogs() {
      return Array.isArray(this.logs) && this.logs.length > 0;
    },
    groupedLogs() {
      if (!Array.isArray(this.logs) || !this.logs.length) {
        return {};
      }

      return this.logs.reduce((acc, log) => {
        const dateKey = this.extractDateLabel(log.time);
        if (!acc[dateKey]) {
          acc[dateKey] = [];
        }
        acc[dateKey].push(log);
        return acc;
      }, {});
    },
    contactsCard() {
      return this.overviewCards.find((card) => card.id === "contacts") || null;
    },
    partnersCard() {
      return this.overviewCards.find((card) => card.id === "partners") || null;
    },
    productsCard() {
      return this.overviewCards.find((card) => card.id === "products") || null;
    },
    leadFormsCard() {
      return this.overviewCards.find((card) => card.id === "leadForms") || null;
    },
    contactsSegmentsAllZero() {
      if (!this.contactsCard || !Array.isArray(this.contactsCard.segments)) {
        return true;
      }

      return this.contactsCard.segments.every((segment) => {
        const valueZero = Number(segment.value) === 0;
        const percentZero = Number(segment.percent) === 0;
        return valueZero && percentZero;
      });
    },
    applicationSegmentsAllZero() {
      if (!this.applicationCard || !Array.isArray(this.applicationCard.segments)) {
        return true;
      }

      return this.applicationCard.segments.every((segment) => Number(segment.value) === 0);
    },
    remainingCards() {
      const primary = new Set([
        "contacts",
        "partners",
        "products",
        "leadForms",
      ]);
      return this.overviewCards.filter((card) => !primary.has(card.id));
    },
    tableCardsByPosition() {
      return {
        left: [
          {
            title: "Data Import",
            titleClass: null,
            rowKey: "category",
            rows: this.dataImportCard.rows,
            columns: [
              {
                key: "category",
                label: "Category",
                align: "left",
                cellClass: "text-slate-700",
              },
              {
                key: "total",
                label: "Total Imports",
                align: "right",
                cellClass: "font-semibold text-slate-900",
              },
              {
                key: "completed",
                label: "Completed",
                align: "right",
                cellClass: "text-slate-500",
              },
              {
                key: "inProgress",
                label: "In-Progress",
                align: "right",
                cellClass: "text-slate-500",
              },
            ],
          },
        ],
        right: [],
      };
    },
  },
  methods: {
    resolveColor(color) {
      if (!color) return "#94a3b8";
      if (color.startsWith("#")) return color;
      const map = {
        "bg-sky-500": "#0ea5e9",
        "bg-emerald-500": "#10b981",
        "bg-amber-400": "#f59e0b",
        "bg-amber-500": "#f59e0b",
        "bg-indigo-500": "#6366f1",
        "bg-rose-500": "#f43f5e",
        "bg-slate-500": "#64748b",
        "bg-slate-600": "#475569",
        "bg-slate-300": "#cbd5f5",
      };
      return map[color] || "#94a3b8";
    },
    async handleDateApply(selection) {
      this.dateFilter = selection;
      await this.fetchUsageData();
    },
    async handleDateCancel() {
      await this.fetchUsageData();
    },
    async handleActivityDateApply(selection) {
      this.activityDateFilter = selection || {
        preset: "today",
        range: { from: "", to: "" },
      };
      this.$emit("activity-date-filter-applied", this.activityDateFilter);
      await this.fetchActivityLog();
    },
    async handleActivityDateCancel() {
      this.$emit("activity-date-filter-cancelled");
      await this.fetchActivityLog();
    },
    handleUserSelect(selection) {
      this.selectedUser = selection;
      this.$emit("user-filter-selected", selection);
    },
    handleUserSelectAll() {
      this.selectedUser = null;
      this.$emit("user-filter-selected", null);
    },
    handleBranchSelect(selection) {
      if (selection === "all") {
        this.selectedBranch = "all";
      } else if (Array.isArray(selection)) {
        this.selectedBranch = [...selection];
      } else if (selection && typeof selection === "object" && selection.id) {
        this.selectedBranch = [selection.id];
      } else if (selection) {
        this.selectedBranch = [selection];
      } else {
        this.selectedBranch = [];
      }
      this.$emit("branch-filter-selected", this.selectedBranch);
    },
    handleBranchSelectAll(value) {
      this.selectedBranch = value || "all";
      this.$emit("branch-filter-selected", this.selectedBranch);
    },
    getApplicationSegmentWidth(value) {
      if (!this.applicationCard || !this.applicationCard.segments.length) {
        return 0;
      }
      const total = this.applicationCard.segments.reduce((sum, segment) => {
        return sum + (Number(segment.value) || 0);
      }, 0);
      const numericValue = Number(value) || 0;
      return total > 0 ? (numericValue / total) * 100 : 0;
    },
    getApplicationSegmentRadiusClass(index) {
      if (!this.applicationCard || !this.applicationCard.segments.length) {
        return "";
      }
      const lastIndex = this.applicationCard.segments.length - 1;
      if (lastIndex === 0) {
        return "rounded-full";
      }
      if (index === 0) {
        return "rounded-l-full";
      }
      if (index === lastIndex) {
        return "rounded-r-full";
      }
      return "";
    },
    getTaskItemWidth(value) {
      if (!this.taskCard || !this.taskCard.items.length) {
        return 0;
      }
      const total = this.taskCard.items.reduce((sum, item) => {
        return sum + (Number(item.value) || 0);
      }, 0);
      const numericValue = Number(value) || 0;
      return total > 0 ? Math.min((numericValue / total) * 100, 100) : 0;
    },
    getDocumentItemWidth(value) {
      if (!this.documentCard || !this.documentCard.items.length) {
        return 0;
      }
      const total = this.documentCard.items.reduce((sum, item) => {
        return sum + (Number(item.value) || 0);
      }, 0);
      const numericValue = Number(value) || 0;
      return total > 0 ? Math.min((numericValue / total) * 100, 100) : 0;
    },
    getNotesItemWidth(value) {
      if (!this.notesTermsCard || !this.notesTermsCard.items.length) {
        return 0;
      }
      const total = this.notesTermsCard.items.reduce((sum, item) => {
        return sum + (Number(item.value) || 0);
      }, 0);
      const numericValue = Number(value) || 0;
      return total > 0 ? Math.min((numericValue / total) * 100, 100) : 0;
    },
    getDocumentChecklistActiveWidth() {
      if (!this.documentChecklistCard) {
        return 0;
      }
      const { active, total } = this.documentChecklistCard;
      const activeValue = Number(active) || 0;
      const totalValue = Number(total) || 0;
      return totalValue > 0
        ? Math.min((activeValue / totalValue) * 100, 100)
        : 0;
    },
    async fetchActivityLog() {
      try {
        const tenantId = this.tenantId;

        if (!tenantId) {
          this.logs = [];
          this.lastUpdatedActivity = null;
          return;
        }

        const filters = {
          dateFilter: this.activityDateFilter,
        };

        let activityPayload = await this.$store.dispatch(
          "TenantAnalyticsStore/fetchTenantActivityLog",
          {
            tenantId,
            filters,
          },
        );
        activityPayload = activityPayload ?? {};
        const activities = Array.isArray(activityPayload.data) ? activityPayload.data : [];

        this.logs = this.transformActivitiesToLogs(activities);
        this.lastUpdatedActivity = activityPayload.last_updated || null;
      } catch (error) {
        this.logs = [];
        this.lastUpdatedActivity = null;
        this.$toast && this.$toast.error("Failed to fetch activity logs");
      }
    },
    transformActivitiesToLogs(activities) {
      if (!Array.isArray(activities) || activities.length === 0) {
        return [];
      }

      return activities.map(this.transformActivityItem);
    },
    transformActivityItem(activity) {
      const user = activity.user || {};

      return {
        ...activity,
        user: {
          ...user,
          name: user.name || "Unknown User",
        },
      };
    },
    buildActivityAction(activity) {
      if (!activity || !activity.action) {
        return "performed an action";
      }

      return activity.action.replace(/[-_]/g, " ");
    },
    generateInitials(name) {
      if (!name) {
        return "--";
      }

      const parts = name.trim().split(" ");
      if (parts.length === 1) {
        return parts[0].charAt(0).toUpperCase();
      }

      return (
        parts[0].charAt(0) + parts[parts.length - 1].charAt(0)
      ).toUpperCase();
    },
    resolveInitialColor(initials) {
      if (!initials) {
        return "bg-blue-700 text-white";
      }

      const colors = [
        "bg-blue-500 text-white",
        "bg-green-500 text-white",
        "bg-red-500 text-white",
        "bg-yellow-500 text-white",
        "bg-purple-500 text-white",
        "bg-pink-500 text-white",
      ];

      const index =
        initials.split("").reduce((sum, char) => sum + char.charCodeAt(0), 0) %
        colors.length;

      return colors[index];
    },
    getInitials(user = {}) {
      const rawInitials =
        typeof user.initials === "string" ? user.initials.trim() : "";

      if (rawInitials) {
        return rawInitials.toUpperCase();
      }

      return this.generateInitials(user.name);
    },
    formatActivityAction(action) {
      if (!action) {
        return "performed an action";
      }

      return action.replace(/[-_]/g, " ");
    },
    extractDateLabel(time) {
      if (!time) {
        return "Unknown Date";
      }

      const [month, day, year] = time.split(" ").filter(Boolean);
      if (!month || !day || !year) {
        return time;
      }

      return `${month} ${day.replace(/,/, "")} ${year}`;
    },
    async fetchUsageData() {
      this.loading = true;
      try {
        const tenantId = this.tenantId;

        if (!tenantId) {
          return;
        }

        const filters = {
          dateFilter: this.dateFilter,
          user: this.selectedUser,
          branch:
            this.selectedBranch === "all"
              ? null
              : Array.isArray(this.selectedBranch) && this.selectedBranch.length === 0
                ? null
                : this.selectedBranch,
        };

        const usageResponse = await this.$store.dispatch(
          "TenantAnalyticsStore/fetchTenantFeatureUsage",
          {
            tenantId,
            filters,
          },
        );
        const usagePayload = usageResponse?.data ?? usageResponse ?? null;

        this.mapUsageDataToAnalytics(usagePayload);
      } catch (error) {
        if (this.$toast && typeof this.$toast.error === "function") {
          this.$toast.error(
            "Error fetching usage data: " + (error.message || "Unknown error"),
          );
        }
      } finally {
        this.loading = false;
      }
    },
    async fetchUsers() {
      this.loadingUsers = true;
      this.errorUsers = null;
      try {
        const tenantId = this.tenantId;

        if (!tenantId) {
          return;
        }

        let users = this.storedTenantUsers;
        if (!Array.isArray(users) || users.length === 0) {
          users = await this.$store.dispatch(
            "TenantAnalyticsStore/fetchTenantUsers",
            tenantId,
          );
        }

        const userList = Array.isArray(users?.data) ? users.data : users;
        this.userOptions = this.mapUsersToOptions(userList || []);
      } catch (error) {
        this.errorUsers = "Failed to load users";
        this.userOptions = [];
      } finally {
        this.loadingUsers = false;
      }
    },
    mapUsersToOptions(apiUsers) {
      const options = [
        {
          id: "all",
          name: "All User",
          email: "Everyone",
        },
      ];

      apiUsers.forEach((user) => {
        options.push({
          id: user.id,
          name: `${user.first_name} ${user.last_name}`.trim(),
          email: user.email,
        });
      });

      return options;
    },
    async fetchBranches() {
      this.loadingBranches = true;
      this.errorBranches = null;
      try {
        const tenantId = this.tenantId;

        if (!tenantId) {
          return;
        }

        let branches = this.storedTenantBranches;
        if (!Array.isArray(branches) || branches.length === 0) {
          branches = await this.$store.dispatch(
            "TenantAnalyticsStore/fetchTenantBranches",
            tenantId,
          );
        }

        const branchList = Array.isArray(branches?.data) ? branches.data : branches;
        this.branchOptions = this.mapBranchesToOptions(branchList || []);
      } catch (error) {
        this.errorBranches = "Failed to load branches";
        this.branchOptions = [];
      } finally {
        this.loadingBranches = false;
      }
    },
    mapBranchesToOptions(apiBranches) {
      const options = [
        {
          id: "all",
          name: "All Branch",
        },
      ];

      apiBranches.forEach((branch) => {
        options.push({
          id: branch.id,
          name: branch.name || branch.branch_name || `Branch ${branch.id}`,
        });
      });

      return options;
    },
    mapUsageDataToAnalytics(apiData) {
      if (!apiData) return;

      const toNumber = (value) => {
        const number = Number(value);
        return Number.isFinite(number) ? number : 0;
      };

      const toPercent = (value, total) => {
        const numericValue = toNumber(value);
        const numericTotal = toNumber(total);
        if (numericTotal <= 0) {
          return 0;
        }
        return Math.round((numericValue / numericTotal) * 100);
      };

      const formatLabel = (label, fallback) => {
        if (label && typeof label === "string") {
          return label;
        }
        return (fallback || "")
          .toString()
          .replace(/[_-]+/g, " ")
          .replace(/\b\w/g, (match) => match.toUpperCase());
      };

      // Contacts overview card
      const contactsPayload = apiData.contactsUsageData || {};
      const contactsCard = this.overviewCards.find((card) => card.id === "contacts");
      if (contactsCard) {
        const totalContacts = toNumber(contactsPayload.totalContacts);
        contactsCard.total = String(totalContacts);
        const segmentMap = [
          {
            label: "Total Clients",
            value: toNumber(contactsPayload.totalClients),
            color: "bg-sky-500",
          },
          {
            label: "Total Prospect",
            value: toNumber(contactsPayload.totalProspects),
            color: "bg-emerald-500",
          },
          {
            label: "Total Enquiries",
            value: toNumber(contactsPayload.totalLeads),
            color: "bg-amber-400",
          },
        ];
        contactsCard.segments = segmentMap.map((segment) => ({
          ...segment,
          value: String(segment.value),
          percent: toPercent(segment.value, totalContacts),
        }));
      }

      if (contactsPayload) {
        const portalActivated = contactsPayload.clientPortalActivated;
        if (portalActivated !== undefined && portalActivated !== null) {
          this.clientsCard.total = toNumber(portalActivated);
          this.clientsCard.label = "Client Portal";
        } else {
          this.clientsCard.total = toNumber(contactsPayload.totalClients);
          this.clientsCard.label = "Clients";
        }
      }

      // Partners overview card
      const partnersPayload = apiData.partnersData || {};
      const partnersCard = this.overviewCards.find((card) => card.id === "partners");
      if (partnersCard) {
        const totalPartners = toNumber(partnersPayload.totalPartners ?? partnersPayload.total);
        partnersCard.total = String(totalPartners);
        const importedPartners = toNumber(
          partnersPayload.totalPartnerImportedFromMaster ?? partnersPayload.totalAutoSyncedPartners,
        );
        const manualPartners = toNumber(partnersPayload.manuallyAddedPartners);
        partnersCard.metrics = [
          {
            label: "Imported from Database",
            value: String(importedPartners),
          },
          {
            label: "Added Manually",
            value: String(manualPartners),
          },
        ];
      }

      // Products overview card
      const productsPayload = apiData.productsData || {};
      const productsCard = this.overviewCards.find((card) => card.id === "products");
      if (productsCard) {
        const totalProducts = toNumber(productsPayload.totalProducts ?? productsPayload.total);
        productsCard.total = String(totalProducts);
        productsCard.metrics = [
          {
            label: "Imported from Database",
            value: String(toNumber(productsPayload.importedProducts)),
          },
          {
            label: "Added Manually",
            value: String(toNumber(productsPayload.manuallyAddedProducts)),
          },
        ];
      }

      // Lead forms overview card
      const leadFormsPayload = apiData.leadFormData || {};
      const leadFormsCard = this.overviewCards.find((card) => card.id === "leadForms");
      if (leadFormsCard) {
        const active = toNumber(leadFormsPayload.activeLeadForms);
        const inactive = toNumber(leadFormsPayload.totalInactiveLeadForms);
        const totalLeadForms = toNumber(leadFormsPayload.totalLeadForms ?? active + inactive);
        leadFormsCard.value = active;
        leadFormsCard.total = totalLeadForms;
        leadFormsCard.metrics = [
          {
            label: "Active Lead Form",
            value: String(active),
            color: "bg-emerald-500",
          },
          {
            label: "Inactive Lead Form",
            value: String(inactive),
            color: "bg-rose-500",
          },
        ];
      }

      // Application card
      const applicationPayload = apiData.applicationsData || {};
      if (Object.keys(applicationPayload).length) {
        this.applicationCard.total = toNumber(applicationPayload.totalApplications);
        const segments = [...this.applicationCard.segments];
        segments.forEach((segment) => {
          if (segment.label === "Discontinued") {
            segment.value = toNumber(applicationPayload.discontinuedApplications);
          } else if (segment.label === "Completed") {
            segment.value = toNumber(applicationPayload.completedApplications);
          } else if (segment.label === "In Progress") {
            segment.value = toNumber(applicationPayload.inProgressApplications);
          }
        });
        this.applicationCard.segments = segments;
      }

      // Interested services card
      const interestedServicesPayload = apiData.interestedServiceData || {};
      if (Object.keys(interestedServicesPayload).length) {
        const totalServices = toNumber(interestedServicesPayload.totalInterestedServices);
        const converted = toNumber(interestedServicesPayload.convertedInterestedServices);
        const draft = toNumber(interestedServicesPayload.draftInterestedServices);
        this.interestedServicesCard.total = totalServices;
        this.interestedServicesCard.conversionRate = totalServices
          ? Number(((converted / totalServices) * 100).toFixed(1))
          : 0;
        this.interestedServicesCard.metrics = [
          { label: "Converted", value: String(converted) },
          { label: "Draft", value: String(draft) },
        ];
      }

      // Task card
      const tasksPayload = apiData.tasksData || {};
      if (Object.keys(tasksPayload).length) {
        this.taskCard.total = toNumber(tasksPayload.totalTasks ?? tasksPayload.total);
        const tasksByModule = apiData.tasksByModuleData?.tasksByModule || {};
        const taskColors = ["#22C55E", "#FACC15", "#3B82F6", "#8B5CF6", "#F97316", "#EC4899", "#14B8A6", "#6366F1", "#EF4444", "#84CC16"];
        this.taskCard.items = Object.entries(tasksByModule)
          .filter(([, v]) => toNumber(v) > 0)
          .map(([label, value], index) => ({
            key: label,
            label,
            value: toNumber(value),
            color: taskColors[index % taskColors.length],
          }));
        this.taskCard.showAll = false;
      }

      // Document card
      const attachmentsPayload = apiData.attachmentsByModuleData || {};
      if (Object.keys(attachmentsPayload).length) {
        const moduleConfig = {
          client:                        { label: "Document in Contacts",               color: "#22C55E" },
          application:                   { label: "Document in Applications",            color: "#FACC15" },
          client_portal:                 { label: "Document in Client Portal",           color: "#3B82F6" },
          task:                          { label: "Document from Task",                  color: "#8B5CF6" },
          email:                         { label: "Document in Emails",                  color: "#F97316" },
          "email-templates":             { label: "Document in Email Templates",         color: "#EC4899" },
          document_checklist:            { label: "Document in Document Checklist",      color: "#14B8A6" },
          partner:                       { label: "Document in Partners",                color: "#6366F1" },
          product:                       { label: "Document in Products",                color: "#EF4444" },
          referrer:                      { label: "Document in Referrers",               color: "#84CC16" },
          automation_task_action:        { label: "Document in Automation Tasks",        color: "#0EA5E9" },
          regulatory_supporting_document:{ label: "Regulatory Supporting Documents",     color: "#A855F7" },
          promotion:                     { label: "Document in Promotions",              color: "#F59E0B" },
        };

        const attachments = attachmentsPayload.attachmentsByModule || {};
        this.documentCard.items = Object.entries(attachments)
          .map(([key, value]) => {
            const config = moduleConfig[key] || {
              label: key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
              color: "#94A3B8",
            };
            return { key, label: config.label, value: toNumber(value), color: config.color };
          })
          .sort((a, b) => b.value - a.value);
        this.documentCard.showAll = false;
        this.documentCard.total = toNumber(attachmentsPayload.totalAttachments);
      }

      // Notes & terms card
      const notesPayload = apiData.notesData || {};
      if (Object.keys(notesPayload).length) {
        const notesByType = notesPayload.notesByType || {};
        this.notesTermsCard.total = toNumber(notesPayload.totalNotes ?? notesPayload.total);
        this.notesTermsCard.items = this.notesTermsCard.items.map((item) => {
          let value = 0;
          if (item.key === "contacts") {
            value = toNumber(notesByType.client);
          } else if (item.key === "applications") {
            value = toNumber(notesByType.application_stage);
          } else if (item.key === "agents") {
            value = toNumber(notesByType.referrer);
          } else if (item.key === "partners") {
            value = toNumber(notesByType.partner);
          }
          return {
            ...item,
            value,
          };
        });
      }

      // Office check-in card
      const officeCheckinPayload = apiData.officeCheckinData || {};
      if (Object.keys(officeCheckinPayload).length) {
        this.officeCheckinCard.items = this.officeCheckinCard.items.map((item) => ({
          ...item,
          value: toNumber(officeCheckinPayload[item.key]),
        }));
      }

      // Appointments card
      const appointmentsPayload = apiData.appointmentsData || {};
      if (Object.keys(appointmentsPayload).length) {
        this.appointmentsCard.total = toNumber(appointmentsPayload.totalAppointments ?? appointmentsPayload.total);
      }

      // Clients card
      if (contactsPayload) {
        this.clientsCard.total = toNumber(contactsPayload.totalClients);
      }

      // Company email card
      const companyEmailPayload = apiData.companyEmailData || {};
      if (Object.keys(companyEmailPayload).length) {
        this.CompanyEmailCard.total = toNumber(companyEmailPayload.totalCompanyEmails ?? companyEmailPayload.total);
      }

      // Classroom card
      const classroomPayload = apiData.classroomData || {};
      if (Object.keys(classroomPayload).length) {
        const mapping = [
          { key: "totalSessions", category: "Total Classroom Session" },
          { key: "totalNotStartedSessions", category: "Not Started Classroom" },
          { key: "totalInprogressSessions", category: "In Progress Classroom" },
          { key: "totalArchivedSessions", category: "Archived Classroom" },
          { key: "totalStudents", category: "Total Enrolled Students" },
        ];
        this.classroomCard.rows = this.classroomCard.rows.map((row) => {
          const match = mapping.find((entry) => entry.category === row.category);
          if (!match) {
            return row;
          }
          return {
            ...row,
            count: toNumber(classroomPayload[match.key]),
          };
        });
      }

      // Automation card
      const automationsPayload = apiData.automationsData || {};
      if (Object.keys(automationsPayload).length) {
        const valueByKey = {
          totalSetup: automationsPayload.totalAutomations,
          active: automationsPayload.activeAutomations,
          inactive: automationsPayload.inactiveAutomations,
          total: automationsPayload.totalAutomations,
        };
        this.automationCard.items = this.automationCard.items.map((item) => ({
          ...item,
          value: toNumber(valueByKey[item.key]),
        }));
      }

      // Events card
      const eventsPayload = apiData.eventsData || {};
      if (Object.keys(eventsPayload).length) {
        const eventsByStatus = eventsPayload.eventsByStatus || [];
        const getStatusCount = (statusKey) => {
          const normalizedKey = (statusKey || "").toLowerCase();
          if (Array.isArray(eventsByStatus)) {
            const match = eventsByStatus.find((item) => {
              const sourceKey = (item.status || item.label || item.key || "").toString().toLowerCase();
              return sourceKey === normalizedKey;
            });
            if (match) {
              return toNumber(match.count ?? match.total ?? match.value);
            }
            return 0;
          }

          if (eventsByStatus && typeof eventsByStatus === "object") {
            const candidateKeys = [
              normalizedKey,
              normalizedKey.toUpperCase(),
              normalizedKey.replace(/\b\w/g, (char) => char.toUpperCase()),
            ];
            for (const key of candidateKeys) {
              if (Object.prototype.hasOwnProperty.call(eventsByStatus, key)) {
                return toNumber(eventsByStatus[key]);
              }
            }
          }

          return 0;
        };

        const valueByKey = {
          total: toNumber(eventsPayload.totalEvents),
          published: getStatusCount("published"),
          drafts: getStatusCount("draft"),
          archived: getStatusCount("archived"),
          contacts: getStatusCount("contacts"),
        };
        this.eventsCard.items = this.eventsCard.items.map((item) => ({
          ...item,
          value: toNumber(valueByKey[item.key]),
        }));
      }

      // Tags card
      const tagsPayload = apiData.tagsData || {};
      if (Object.keys(tagsPayload).length) {
        this.tagsAddedCard.total = toNumber(tagsPayload.totalTags ?? tagsPayload.total);
      }

      // Workflow card
      const workflowPayload = apiData.workflowsData || {};
      if (Object.keys(workflowPayload).length) {
        this.workflowCard.total = toNumber(workflowPayload.totalWorkflows ?? workflowPayload.total);
        this.workflowCard.value = toNumber(workflowPayload.activeWorkflows);
        this.workflowCard.metrics = this.workflowCard.metrics.map((metric) => {
          if (metric.label === "Active Workflow") {
            return {
              ...metric,
              value: String(toNumber(workflowPayload.activeWorkflows)),
              color: "bg-emerald-500",
            };
          }
          if (metric.label === "Inactive Workflow") {
            return {
              ...metric,
              value: String(toNumber(workflowPayload.inactiveWorkflows)),
              color: "bg-sky-500",
            };
          }
          return metric;
        });
      }

      // Quotation card
      const quotationPayload = apiData.quotationsData || {};
      if (Object.keys(quotationPayload).length) {
        const totals = quotationPayload.totalQuotations || {};
        this.quotationCard.items = this.quotationCard.items.map((item) => {
          if (item.key === "template") {
            return { ...item, value: toNumber(quotationPayload.totalQuotationTemplates) };
          }
          if (item.key === "created") {
            return { ...item, value: toNumber(totals.totalQuotations) };
          }
          if (item.key === "sent") {
            return { ...item, value: toNumber(totals.sentQuotations) };
          }
          return item;
        });
      }

      // SMS usage card
      const smsUsagePayload = apiData.smsUsages || apiData.smsUsage || {};
      if (Object.keys(smsUsagePayload).length) {
        const totalPurchased = toNumber(smsUsagePayload.totalSmsUsages ?? smsUsagePayload.totalSmsQuotaPurchased);
        const used = toNumber(smsUsagePayload.totalSmsSent ?? smsUsagePayload.used);
        const remaining = toNumber(
          smsUsagePayload.totalSmsReceived ??
          smsUsagePayload.remaining ??
          Math.max(totalPurchased - used, 0)
        );
        this.smsUsageCard.items = this.smsUsageCard.items.map((item) => {
          if (item.key === "total") {
            return { ...item, value: totalPurchased };
          }
          if (item.key === "remaining") {
            return { ...item, value: remaining };
          }
          if (item.key === "used") {
            return { ...item, value: used };
          }
          return item;
        });
      }

      // WhatsApp usage card
      const whatsappUsagePayload = apiData.whatsappUsages || apiData.whatsAppUsage || {};
      if (Object.keys(whatsappUsagePayload).length) {
        const sent = toNumber(whatsappUsagePayload.totalWhatsappSent);
        const received = toNumber(whatsappUsagePayload.totalWhatsappReceived);
        const isActive = sent > 0 || received > 0;
        this.whatsAppUsageCard.statusLabel = isActive ? "Active" : "Not Integrated";
        this.whatsAppUsageCard.statusColor = isActive ? "#16A34A" : "#DC2626";
        this.whatsAppUsageCard.statusBackground = isActive ? "#DCFCE7" : "#FEF2F2";
        this.whatsAppUsageCard.items = this.whatsAppUsageCard.items.map((item) => {
          if (item.key === "sent") {
            return { ...item, value: sent };
          }
          if (item.key === "received") {
            return { ...item, value: received };
          }
          return item;
        });
      }

      // Email usage card
      const emailUsagePayload = apiData.emailUsagesData || {};
      if (Object.keys(emailUsagePayload).length) {
        this.emailUsageCard.items = this.emailUsageCard.items.map((item) => {
          if (item.key === "sent") {
            return { ...item, value: toNumber(emailUsagePayload.sent) };
          }
          if (item.key === "received") {
            return { ...item, value: toNumber(emailUsagePayload.received) };
          }
          return item;
        });
      }

      // Campaign cards
      const campaignsPayload = apiData.campaignsData || {};
      if (Object.keys(campaignsPayload).length) {
        const smsStatus = campaignsPayload.smsCampaignByStatus || [];
        const emailStatus = campaignsPayload.emailCampaignByStatus || {};
        const whatsappStatus = campaignsPayload.whatsappCampaignByStatus || [];

        const mapStatus = (segments, source) =>
          segments.map((segment) => {
            let value = 0;
            if (Array.isArray(source)) {
              const match = source.find((item) => (item.status || item.label || "").toLowerCase() === segment.key);
              value = toNumber(match?.count || match?.value);
            } else if (source && typeof source === "object") {
              value = toNumber(source[segment.key]);
            }
            return {
              ...segment,
              value,
            };
          });

        this.smsCampaignCard.total = toNumber(campaignsPayload.totalSmsCampaigns ?? campaignsPayload.totalCampaigns);
        this.smsCampaignCard.chartSegments = mapStatus(this.smsCampaignCard.chartSegments, smsStatus);

        this.emailCampaignCard.total = toNumber(campaignsPayload.totalEmailCampaigns);
        this.emailCampaignCard.chartSegments = mapStatus(this.emailCampaignCard.chartSegments, emailStatus);

        this.whatsAppCampaignCard.total = toNumber(campaignsPayload.totalWhatsappCampaigns);
        this.whatsAppCampaignCard.chartSegments = mapStatus(this.whatsAppCampaignCard.chartSegments, whatsappStatus);
      }

      // Campaign delivery cards
      const emailDeliveryPayload = apiData.emailCampaignByDeliveryData || {};
      this.campaignDeliveryCards = this.campaignDeliveryCards.map((card) => {
        if (card.title === "Email Campaign By Delivery") {
          return {
            ...card,
            totalValue: toNumber(emailDeliveryPayload.totalEmailCampaigns ?? card.totalValue),
          };
        }
        return card;
      });

      // Agents card
      const agentsPayload = apiData.agentsData || {};
      if (Object.keys(agentsPayload).length) {
        this.agentsCard.total = toNumber(agentsPayload.totalAgents ?? agentsPayload.total);
        this.agentsCard.outerValue = toNumber(agentsPayload.superAgents);
        this.agentsCard.innerValue = toNumber(agentsPayload.subAgents);
        this.agentsCard.outerTotal = this.agentsCard.total;
        this.agentsCard.innerTotal = this.agentsCard.total;
        this.agentsCard.metrics = this.agentsCard.metrics.map((metric) => {
          if (metric.label === "Super Agents") {
            return { ...metric, value: toNumber(agentsPayload.superAgents) };
          }
          if (metric.label === "Sub Agents") {
            return { ...metric, value: toNumber(agentsPayload.subAgents) };
          }
          return metric;
        });
      }

      // Agent connect card
      const agentConnectPayload = apiData.agentConnectData || {};
      if (Object.keys(agentConnectPayload).length) {
        const valueByKey = {
          connected: agentConnectPayload.total_agent_connected,
          applicationsReceived: agentConnectPayload.total_application_received,
          applicationsSent: agentConnectPayload.total_application_sent,
          requestSent: agentConnectPayload.total_request_sent,
          documentsExchanged: agentConnectPayload.total_documents_shared,
          comments: agentConnectPayload.total_comment_added,
        };
        this.agentConnectCard.items = this.agentConnectCard.items.map((item) => ({
          ...item,
          value: toNumber(valueByKey[item.key]),
        }));
      }

      // Agent portal card
      const agentPortalPayload = apiData.agentPortalData || {};
      if (Object.keys(agentPortalPayload).length) {
        const valueByKey = {
          licensePurchased: agentPortalPayload.total_licenses_purchased,
          totalActivatedPortals: agentPortalPayload.total_activated_portals,
          activePortals: agentPortalPayload.active_portals,
          applicationsExchanged: agentPortalPayload.applications_exchanged,
        };
        this.agentPortalCard.items = this.agentPortalCard.items.map((item) => ({
          ...item,
          value: toNumber(valueByKey[item.key]),
        }));
      }

      // Data import card
      const dataImportedPayload = apiData.dataImported || {};
      if (Object.keys(dataImportedPayload).length) {
        const rowValueByCategory = {
          "Partner & Product": dataImportedPayload.partnersDataImported || {},
          Contact: dataImportedPayload.contactsDataImported || {},
          Application: dataImportedPayload.applicationsDataImported || {},
        };
        this.dataImportCard.rows = this.dataImportCard.rows.map((row) => {
          const source = rowValueByCategory[row.category] || {};
          if (Array.isArray(source)) {
            const aggregate = source.reduce(
              (acc, item) => ({
                total: acc.total + toNumber(item.total),
                completed: acc.completed + toNumber(item.completed),
                in_progress: acc.in_progress + toNumber(item.in_progress ?? item.inProgress ?? item.queue),
              }),
              { total: 0, completed: 0, in_progress: 0 },
            );
            return {
              ...row,
              total: aggregate.total,
              completed: aggregate.completed,
              inProgress: aggregate.in_progress,
            };
          }
          return {
            ...row,
            total: toNumber(source.total),
            completed: toNumber(source.completed),
            inProgress:
              toNumber(source.in_progress ?? source.inProgress ?? 0),
          };
        });
        this.dataImportCard.total = toNumber(dataImportedPayload.totalDataImported);
      }

      // Invoice card
      const invoicePayload = apiData.invoiceData || {};
      if (Object.keys(invoicePayload).length) {
        this.invoiceCard.total = toNumber(invoicePayload.totalInvoices ?? invoicePayload.total);
        const invoiceRows = invoicePayload.invoices || {};
        this.invoiceCard.rows = Object.entries(invoiceRows).map(([key, value]) => ({
          type: formatLabel(value?.label, key),
          paid: toNumber(value?.statuses?.Paid),
          unpaid: toNumber(value?.statuses?.Unpaid),
        }));
      }

      // Document checklist card
      const documentChecklistPayload = apiData.documentChecklistData || {};
      if (Object.keys(documentChecklistPayload).length) {
        const status = documentChecklistPayload.documentChecklistByStatus || {};
        this.documentChecklistCard.total = toNumber(
          documentChecklistPayload.totalDocumentChecklist ?? documentChecklistPayload.total,
        );
        this.documentChecklistCard.active = toNumber(status.Active);
        this.documentChecklistCard.inactive = toNumber(status.Inactive);
        this.documentChecklistCard.activeDocumentType = toNumber(status.Active);
      }

      // Payment schedule card
      const paymentSchedulePayload = apiData.paymentScheduleData || {};
      if (Object.keys(paymentSchedulePayload).length) {
        this.paymentScheduleCard.total = toNumber(
          paymentSchedulePayload.totalPaymentSchedule ?? paymentSchedulePayload.total,
        );
      }

      // Template card
      const templatesPayload = apiData.templatesData || {};
      if (Object.keys(templatesPayload).length) {
        this.templateCard.items = this.templateCard.items.map((item) => {
          if (item.key === "sms") {
            return { ...item, value: toNumber(templatesPayload.smsTemplates) };
          }
          if (item.key === "email") {
            return { ...item, value: toNumber(templatesPayload.emailTemplates) };
          }
          return item;
        });
      }
    },
  },
  watch: {
    selectedUser() {
      this.fetchUsageData();
    },
    selectedBranch() {
      this.fetchUsageData();
    },
  },
  async mounted() {
    if (this.tenantId) {
      this.loading = true;
      await this.fetchUsers();
      await this.fetchBranches();
      await this.fetchActivityLog();
      await this.fetchUsageData();
      this.loading = false;
    }
  },
};
</script>
